import { useLiveQuery } from "dexie-react-hooks";
import { CalendarCheck2, CalendarRange, CircleCheck, Flame, Palmtree, Sparkles, Timer, Trophy, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { colorHex, type Stats } from "@todoer/shared";
import { Card, cx, EmptyState, Input, PageHeader, PropertyRow, Spinner, Switch } from "../components/ui.js";
import { api } from "../lib/api.js";
import { db, useUser } from "../lib/db.js";
import { cmd, dispatch, onRemoteChange } from "../lib/sync.js";

function Ring({ value, goal, label }: { value: number; goal: number; label: string }) {
  const pct = goal > 0 ? Math.min(1, value / goal) : 0;
  const R = 40;
  const C = 2 * Math.PI * R;
  const met = pct >= 1;
  return (
    <div className="flex flex-col items-center rounded-card border border-border bg-surface-raised p-4">
      <div className="relative h-28 w-28" role="img" aria-label={`${label}: ${value} of ${goal}${met ? ", goal met" : ""}`}>
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden>
          <circle cx="50" cy="50" r={R} fill="none" strokeWidth="8" stroke="var(--color-surface-sunken)" />
          <circle cx="50" cy="50" r={R} fill="none" strokeWidth="8" strokeLinecap="round" stroke={met ? "var(--color-success)" : "var(--color-brand)"}
            strokeDasharray={C} strokeDashoffset={C * (1 - pct)} style={{ transition: "stroke-dashoffset 600ms var(--ease-out)" }} />
        </svg>
        <div className="absolute inset-0 grid place-items-center text-center" aria-hidden>
          <div><div className="tabular text-title font-semibold">{value}</div><div className="tabular text-meta text-text-faint">of {goal}</div></div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1 text-body font-medium">{label}{met && <CircleCheck size={14} className="text-success" aria-label="goal met" />}</div>
    </div>
  );
}

function Tile({ icon: Icon, value, label, sub, tone }: { icon: LucideIcon; value: React.ReactNode; label: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-card border border-border bg-surface-raised p-4">
      <div className="flex items-center gap-2 text-meta font-medium text-text-muted"><Icon size={16} aria-hidden className={tone} />{label}</div>
      <div className="tabular mt-2 text-subtitle font-semibold">{value}</div>
      {sub && <div className="text-meta text-text-faint">{sub}</div>}
    </div>
  );
}

const fmtDay = (d: string, opts: Intl.DateTimeFormatOptions) => new Date(`${d}T00:00`).toLocaleDateString([], opts);

function DailyChart({ days, goal }: { days: Stats["days"]; goal: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, goal, ...days.map((d) => d.count));
  const total = days.reduce((n, d) => n + d.count, 0);
  const h = hover !== null ? days[hover] : null;
  return (
    <div>
      <div className="relative flex gap-2">
        <div className="tabular flex h-44 w-6 flex-col justify-between text-right text-caption text-text-faint" aria-hidden>
          <span>{max}</span><span>{Math.round(max / 2)}</span><span>0</span>
        </div>
        <div className="relative h-44 min-w-0 flex-1 border-b border-border" role="list" aria-label={`Tasks completed per day over 4 weeks, ${total} in total`}>
          <div aria-hidden className="absolute inset-x-0 top-0 border-t border-dashed border-border" />
          <div aria-hidden className="absolute inset-x-0 top-1/2 border-t border-dashed border-border" />
          {goal > 0 && (
            <div aria-hidden className="absolute inset-x-0 z-10 border-t-2 border-dashed border-text-muted" style={{ bottom: `${(goal / max) * 100}%` }}>
              <span className="absolute -top-5 right-0 rounded bg-surface px-1 text-caption font-medium text-text-muted">Goal {goal}</span>
            </div>
          )}
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {days.map((d, i) => (
              <button key={d.date} type="button" role="listitem" aria-label={`${fmtDay(d.date, { weekday: "long", day: "numeric", month: "long" })}: ${d.count} completed`}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                className="group relative flex h-full flex-1 items-end outline-none">
                <span className={cx("block w-full rounded-t-[4px] transition-opacity", hover !== null && hover !== i && "opacity-50")}
                  style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? 3 : 0, background: "var(--color-brand)" }} />
                <span aria-hidden className="absolute inset-x-0 bottom-0 top-0 rounded-[4px] group-focus-visible:ring-2 group-focus-visible:ring-focus" />
              </button>
            ))}
          </div>
          {h && hover !== null && (
            <div className="pointer-events-none absolute z-20 -translate-x-1/2 rounded-control bg-text px-2 py-1 text-meta text-surface shadow-pop"
              style={{ left: `${((hover + 0.5) / days.length) * 100}%`, bottom: `calc(${(h.count / max) * 100}% + 8px)` }}>
              <div className="whitespace-nowrap font-semibold">{fmtDay(h.date, { weekday: "short", day: "numeric", month: "short" })}</div>
              <div className="tabular whitespace-nowrap">{h.count} completed{goal > 0 && h.count >= goal ? " · goal met" : ""}</div>
            </div>
          )}
        </div>
      </div>
      <div className="ml-8 flex text-caption text-text-faint" aria-hidden>
        {days.map((d, i) => <span key={d.date} className="flex-1 whitespace-nowrap">{i % 7 === 0 ? fmtDay(d.date, { day: "numeric", month: "short" }) : ""}</span>)}
      </div>
    </div>
  );
}

export function Productivity() {
  const user = useUser();
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);

  useEffect(() => {
    const load = () => api<Stats>("/api/stats").then((s) => { setStats(s); setError(null); }).catch((e: Error) => setError(e.message));
    load();
    return onRemoteChange(load);
  }, []);

  const top = Math.max(1, ...(stats?.byProject.map((x) => x.count) ?? [1]));
  return (
    <div>
      <PageHeader title="Productivity" subtitle={stats ? <span className="inline-flex items-center gap-1.5"><Sparkles size={14} className="text-brand-text" aria-hidden />{stats.karma.toLocaleString()} karma · {stats.karmaLevel}</span> : undefined} />
      {error && <p className="mb-4 rounded-card bg-danger-soft px-3 py-2 text-body text-danger">{error}. Statistics need a connection.</p>}
      {!stats && !error && <div className="grid h-40 place-items-center"><Spinner size={20} /></div>}
      {stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Ring value={stats.completedToday} goal={stats.dailyGoal} label="Today" />
            <Ring value={stats.completedThisWeek} goal={stats.weeklyGoal} label="This week" />
            <Tile icon={Flame} tone="text-p2" value={`${stats.dailyStreak.current} days`} label="Daily streak" sub={`Best ${stats.dailyStreak.best}`} />
            <Tile icon={Trophy} tone="text-p2" value={`${stats.weeklyStreak.current} weeks`} label="Weekly streak" sub={`Best ${stats.weeklyStreak.best}`} />
          </div>

          <Card title="Completed in the last 4 weeks" description={`${stats.days.reduce((n, d) => n + d.count, 0)} tasks · dashed line is your daily goal`}>
            <DailyChart days={stats.days} goal={stats.dailyGoal} />
          </Card>

          <div className="grid gap-6 md:grid-cols-2">
            <Card title="By project" description="Completed in the last 4 weeks">
              {stats.byProject.length ? (
                <ul className="space-y-3">
                  {[...stats.byProject].sort((a, b) => b.count - a.count).map((r) => {
                    const p = projects.find((x) => x.id === r.projectId);
                    const color = p?.isInbox ? "var(--color-info)" : colorHex(p?.color ?? "grey");
                    return (
                      <li key={r.projectId} className="text-body">
                        <div className="mb-1 flex items-center gap-2">
                          <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />
                          <span className="min-w-0 flex-1 truncate">{p ? (p.isInbox ? "Inbox" : p.name) : "Deleted project"}</span>
                          <span className="tabular font-medium">{r.count}</span>
                        </div>
                        <div className="h-2 rounded-full bg-surface-sunken"><div className="h-2 rounded-full" style={{ width: `${(r.count / top) * 100}%`, background: color }} /></div>
                      </li>
                    );
                  })}
                </ul>
              ) : <EmptyState compact kind="tasks" title="No completions yet">Complete some tasks to see a breakdown.</EmptyState>}
            </Card>
            <div className="grid grid-cols-2 gap-3 self-start">
              <Tile icon={CircleCheck} tone="text-success" value={stats.totalCompleted.toLocaleString()} label="Tasks completed" sub="All time" />
              <Tile icon={Timer} tone="text-brand-text" value={`${stats.focusMinutesToday} min`} label="Focus today" />
              <Tile icon={CalendarRange} tone="text-brand-text" value={`${stats.focusMinutesWeek} min`} label="Focus this week" />
              <Tile icon={Sparkles} tone="text-brand-text" value={stats.karma.toLocaleString()} label="Karma" sub={stats.karmaLevel} />
            </div>
          </div>
        </div>
      )}

      {user && (
        <Card className="mt-6" title="Goals" description="Rings fill as you complete tasks. Streaks count days or weeks you hit the goal.">
          <div className="space-y-1">
            <PropertyRow icon={CalendarCheck2} label="Daily goal">
              <Input type="number" min={0} max={100} aria-label="Daily goal" className="tabular w-24" value={user.dailyGoal}
                onChange={(e) => void dispatch(cmd("user_update", { dailyGoal: Math.max(0, Number(e.target.value) || 0) }))} />
            </PropertyRow>
            <PropertyRow icon={CalendarRange} label="Weekly goal">
              <Input type="number" min={0} max={700} aria-label="Weekly goal" className="tabular w-24" value={user.weeklyGoal}
                onChange={(e) => void dispatch(cmd("user_update", { weeklyGoal: Math.max(0, Number(e.target.value) || 0) }))} />
            </PropertyRow>
            <PropertyRow icon={Palmtree} label="Vacation">
              <Switch label="Vacation mode" description="Keeps streaks and pauses the daily summary." checked={user.vacationMode}
                onChange={(v) => void dispatch(cmd("user_update", { vacationMode: v }))} />
            </PropertyRow>
          </div>
        </Card>
      )}
    </div>
  );
}
